import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectActivePlaidFinancialAccounts } from '@/selectors/financeSelectors'

import {
  useReselector,
  useToggle,
  useTrackFinancialInsightsButtonClick,
} from '@/utils/sharedHooks'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { Text, Alert, Link, HeroCard } from '@/ui'
import { ArticleCard } from '../shared/ArticleCard'
import {
  ARTICLE_TYPE,
  FinancialInsightsButtonType,
  INSIGHTS_TYPE,
} from '../shared/utils'
import { UncategorizedTransactionBanner } from '../shared/UncategorizedTransactionBanner'
import { CurrentPanel } from '../shared/CurrentPanel'
import { TrackInsightsSection } from '../shared/TrackInsightsSection'
import {
  useInsightsDatePicker,
  InsightsDatePicker,
} from '../shared/InsightsDatePicker'
import { InsightBreadcrumb } from '../shared/InsightBreadcrumb'
import { ImageBackgroundColors, TypographyStyles } from '@/styles'

export const InsightsProfitPanel = () => {
  const dispatch = useAppDispatch()
  const [displayBanner, toggleBanner] = useToggle(true)

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const trackInsights = useTrackFinancialInsightsButtonClick()

  const [searchParams] = useSearchParams()

  const { monthParam, yearParam } = useMemo(() => {
    return {
      monthParam: searchParams.get('month'),
      yearParam: searchParams.get('year'),
    }
  }, [searchParams])

  const { yearFilter, monthFilter, inputDate, setYearFilter, setMonthFilter } =
    useInsightsDatePicker(monthParam, yearParam)

  useEffect(() => {
    dispatch(fetchFinancialAccountsIfNeeded())
  }, [dispatch])

  const linkedPlaidAccounts = useReselector(selectActivePlaidFinancialAccounts)
  const hasLinkedPlaidAccounts = !isEmpty(linkedPlaidAccounts)

  const closeBanner = () => {
    toggleBanner()
    trackInsights({
      button: FinancialInsightsButtonType.CLOSE_BANNER,
      insightsType: INSIGHTS_TYPE.PROFIT,
    })
  }

  return (
    <div className="space-y-6">
      <InsightBreadcrumb month={monthFilter} year={yearFilter} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Text variant={TypographyStyles.H1}>Profit</Text>
        <InsightsDatePicker
          monthFilter={monthFilter}
          yearFilter={yearFilter}
          handleMonthFilterChange={setMonthFilter}
          handleYearFilterChange={setYearFilter}
          insightsType={INSIGHTS_TYPE.INCOME}
        />
      </div>

      {displayBanner && (
        <HeroCard
          title="What is Profit?"
          imageSrc="https://heard-images.s3.amazonaws.com/assets/coins-stack.svg"
          imageAlt="What is Profit?"
          imageColor={ImageBackgroundColors.GREEN}
          onClose={closeBanner}
        >
          <Text variant={TypographyStyles.BODY_SM}>
            Your profit is the income you make minus your business expenses.
            It&apos;s the amount of money you have left after you have paid off
            all your business expenses each month.
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            Building a profitable practice is the same as building a practice
            that can last, where you are taking care of yourself as well as your
            clients. Profit affects how much you are able to pay yourself
            (Owner&apos;s Draw) and save for things like retirement.
          </Text>
        </HeroCard>
      )}

      {!hasLinkedPlaidAccounts && (
        <Alert>
          <Text variant={TypographyStyles.BODY_SM}>
            You do not have a connected bank account.{' '}
            <Link href="/accounts#connected-institutions" newPage={false}>
              Link Business Account
            </Link>
          </Text>
        </Alert>
      )}

      <UncategorizedTransactionBanner inputDate={inputDate} />

      <CurrentPanel insightsType={INSIGHTS_TYPE.PROFIT} inputDate={inputDate} />
      <TrackInsightsSection type={INSIGHTS_TYPE.PROFIT} inputDate={inputDate} />

      <Text variant={TypographyStyles.H2} className="mb-3">
        How can I build a profitable practice?
      </Text>

      <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
        <ArticleCard
          title="How To Build a Profitable Therapy Practice"
          type={ARTICLE_TYPE.ARTICLE}
          url="https://www.joinheard.com/articles/how-to-build-a-profitable-therapy-practice"
          insightsType={INSIGHTS_TYPE.PROFIT}
        />
        <ArticleCard
          title="How To Keep Your Therapy Practice Profitable"
          type={ARTICLE_TYPE.ARTICLE}
          url="https://www.joinheard.com/articles/how-to-keep-your-therapy-practice-profitable"
          insightsType={INSIGHTS_TYPE.PROFIT}
        />
        <ArticleCard
          title="The Complete Guide to Financial Planning for Therapists"
          type={ARTICLE_TYPE.ARTICLE}
          url="https://www.joinheard.com/articles/the-complete-guide-to-financial-planning-for-therapists"
          insightsType={INSIGHTS_TYPE.PROFIT}
        />
      </div>
    </div>
  )
}
